import React, { useEffect, useState } from "react";
import { Card, Form, InputNumber, Button, Typography, Alert, Spin, message } from "antd";
import { Letter1 } from "../Letter";
import {
  LETTER_PRINT_DEFAULTS,
  LETTER_PRINT_BOUNDS,
  resolveLetterPrintSettings,
} from "../Letter/letterPrintDefaults";
import useLetterSettingsQuery from "../../api/queries/useLetterSettingsQuery";
import useLetterSettingsMutation from "../../api/mutations/useLetterSettingsMutation";

const { Paragraph, Text } = Typography;

const PX_PER_MM = 96 / 25.4;
const SCALE = 0.42;
const mmToPx = (mm) => mm * PX_PER_MM * SCALE;
const PAGE_W = mmToPx(210);
const PAGE_H = mmToPx(297);

// Sample data so the preview mirrors a real change-of-address letter.
const SAMPLE = {
  name: "Lindani Phungula",
  companyName: "NHS Blood and Transplant",
  companyAddress: {
    address1: "NHS BT, 500-600 North Bristol Park",
    address2: "Northway, Filton",
    town: "Bristol",
    postCode: "BS34 7QH",
  },
  newAddress: "5 Duncombe Road,Northchurch,Berkhamsted,HP4 3RG",
  oldAddress: "1 Glen Mews,Southend-on-Sea,SS1 2FS",
  movingDate: "2026-05-25",
  dob: "1990-06-04",
  email: "lindani.phungula@moveinout.co.uk",
  phone: "07950876836",
  serviceName: "NHS",
  required: {},
  requiredDetails: [],
};

const getRole = () => {
  try {
    return JSON.parse(localStorage.getItem("adminData") || "{}").role;
  } catch (e) {
    return undefined;
  }
};

const FIELDS = [
  {
    key: "addressTopMm",
    label: "Address top offset (mm)",
    help: "Moves the recipient address down the page. Increase if the address sits above the window.",
  },
  {
    key: "addressDateGapMm",
    label: "Address → date gap (mm)",
    help: "Space between the address block and the date. Keeps the date below the window.",
  },
  {
    key: "leftOffsetMm",
    label: "Left offset (mm)",
    help: "Moves the address right from the page edge (window starts 20mm from the left).",
  },
];

const LetterPrintSettings = () => {
  const isSuperAdmin = getRole() === "super_admin";
  const [form] = Form.useForm();
  const { data, isLoading } = useLetterSettingsQuery();
  const [preview, setPreview] = useState(LETTER_PRINT_DEFAULTS);

  const save = useLetterSettingsMutation(() =>
    message.success("Letter layout saved")
  );

  useEffect(() => {
    if (data) {
      const resolved = resolveLetterPrintSettings(data);
      form.setFieldsValue(resolved);
      setPreview(resolved);
    }
  }, [data, form]);

  const onValuesChange = (_, all) => setPreview(resolveLetterPrintSettings(all));

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      save.mutate(values);
    } catch (e) {
      if (e?.errorFields) return;
      message.error("Could not save letter layout");
    }
  };

  const onRestore = () => {
    form.setFieldsValue(LETTER_PRINT_DEFAULTS);
    setPreview(LETTER_PRINT_DEFAULTS);
    save.mutate(LETTER_PRINT_DEFAULTS);
  };

  return (
    <Card title="Letter Print Layout" style={{ maxWidth: 900, marginTop: 24 }}>
      <Paragraph>
        Position the recipient address inside the mailing window for Whistl "PDF to Post".
        These values apply to the change-of-address letter for <Text strong>all admins</Text>.
      </Paragraph>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="When printing, use Default margins, 100% scale, A4 — the address position depends on the browser print dialog, which the app can't set."
      />
      {!isSuperAdmin && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="Only super admins can change these values. Showing the current org settings."
        />
      )}

      {isLoading ? (
        <Spin />
      ) : (
        <div style={{ display: "flex", gap: 32, flexWrap: "wrap" }}>
          {/* Controls */}
          <div style={{ flex: "1 1 280px", minWidth: 260 }}>
            <Form
              form={form}
              layout="vertical"
              initialValues={LETTER_PRINT_DEFAULTS}
              onValuesChange={onValuesChange}
              disabled={!isSuperAdmin}
            >
              {FIELDS.map((f) => (
                <Form.Item
                  key={f.key}
                  name={f.key}
                  label={f.label}
                  extra={f.help}
                  rules={[
                    { required: true, message: "Required" },
                    {
                      type: "number",
                      min: LETTER_PRINT_BOUNDS[f.key].min,
                      max: LETTER_PRINT_BOUNDS[f.key].max,
                      message: `${LETTER_PRINT_BOUNDS[f.key].min}-${LETTER_PRINT_BOUNDS[f.key].max} mm`,
                    },
                  ]}
                >
                  <InputNumber
                    min={LETTER_PRINT_BOUNDS[f.key].min}
                    max={LETTER_PRINT_BOUNDS[f.key].max}
                    step={1}
                    addonAfter="mm"
                    style={{ width: 140 }}
                  />
                </Form.Item>
              ))}
              {isSuperAdmin && (
                <div style={{ display: "flex", gap: 8 }}>
                  <Button type="primary" onClick={onSave} loading={save.isLoading}>
                    Save
                  </Button>
                  <Button onClick={onRestore} disabled={save.isLoading}>
                    Restore Defaults
                  </Button>
                </div>
              )}
            </Form>
          </div>

          {/* Live preview of the letter with the current print settings applied */}
          <div style={{ flex: "0 0 auto" }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Preview — how the change-of-address letter will look with these settings.
            </Text>
            <div
              className="letter-preview-scope"
              style={{
                position: "relative",
                width: PAGE_W,
                height: PAGE_H,
                marginTop: 8,
                border: "1px solid #d9d9d9",
                overflow: "hidden",
                background: "#fff",
              }}
            >
              <style>{".letter-preview-scope page{margin:0 !important;box-shadow:none !important}"}</style>
              <div
                style={{
                  transform: `scale(${SCALE})`,
                  transformOrigin: "top left",
                  width: 210 * PX_PER_MM,
                }}
              >
                <Letter1 {...SAMPLE} printSettings={preview} />
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

export default LetterPrintSettings;
